import React from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import UserSettingsMyAccountTab from 'components/accounts/UserSettingsMyAccountTab';
import UserSettingsRolesAndOrganisationsTab from '../../components/accounts/UserSettingsRolesAndOrganisationTab';
import { Container } from 'styles/Styles.styled';
import { withGreyBackground } from 'pages/v2/WithGreyBackground';
import AuthService from 'services/AuthService';
import useCurrentUser from 'hooks/useCurrentUser';
import EpdRoutes, { EpdLinks } from 'routes/EpdRoutes';
import FontIcon from 'components/icons/FontIcon';

const UserSettings: React.FC = () => {
  const { tab } = useParams<{ tab: 'account' | 'membership' | undefined }>();
  const history = useHistory();

  const activeTab = tab || 'account';

  const userModel = useCurrentUser();

  const handleLogout = () => {
    try {
      AuthService.logout(history);
    } catch {
      history.push(EpdRoutes.login);
    }
  };

  return (
    <Container>
      <TitleDiv>User settings</TitleDiv>
      <ContentsGrid>
        <SettingsMenuContainer>
          <MenuItem to={EpdLinks.userSettings('account')} $active={activeTab === 'account'}>
            <FontIcon icon="user" />
            My account
          </MenuItem>
          {process.env.REACT_APP_DEBUG && (
            <MenuItem to={EpdLinks.userSettings('membership')} $active={activeTab === 'membership'}>
              <FontIcon icon="users" />
              Roles & Organisations
            </MenuItem>
          )}
          <LogoutWrapper>
            <Logout onClick={handleLogout}>
              <FontIcon icon="log-out" />
              Log out
            </Logout>
          </LogoutWrapper>
        </SettingsMenuContainer>
        <div>
          {activeTab === 'account' && <UserSettingsMyAccountTab />}
          {activeTab === 'membership' && userModel && (
            <UserSettingsRolesAndOrganisationsTab userId={userModel.id} memberships={userModel.memberships || []} />
          )}
        </div>
      </ContentsGrid>
    </Container>
  );
};

const TitleDiv = styled.div`
  ${(props) => props.theme.typography.headerH2}
  flex-wrap: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0 0 2rem 0;
`;

const ContentsGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  column-gap: 2rem;
  row-gap: 1rem;

  ${(props) => props.theme.media.desktop} {
    grid-template-columns: max-content minmax(0, 1fr);
  }
`;

const SettingsMenuContainer = styled.div`
  ${(props) => props.theme.media.desktop} {
    height: 670px;
  }
  min-width: 252px;
  display: flex;
  flex-direction: column;
  gap: 1em;
  border-radius: 4px;
  box-shadow: 0px 4px 6px 0px rgba(0, 0, 0, 0.04);
  padding: 1rem;
  background-color: ${(props) => props.theme.colors.primaryWhite};
`;

const MenuItem = styled(Link) <{ $active?: boolean }>`
  padding: 12px 16px;
  line-height: 1;
  color: ${(props) => (props.$active ? props.theme.colors.primaryGreen : props.theme.colors.primaryBlack)};
  display: flex;
  gap: 1em;
  align-items: center;
  background-color: ${(props) =>
    props.$active ? props.theme.colors.secondaryLightGreen30 : props.theme.colors.primaryWhite};
  text-decoration: none;

  &:hover,
  &:focus-visible {
    color: ${(props) => props.theme.colors.primaryBlack};
    background-color: ${(props) => props.theme.colors.lightGray};
    z-index: 1;
  }
`;

const LogoutWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const Logout = styled.button`
  padding: 12px 16px;
  font-size: inherit;
  line-height: 1;
  font-family: inherit;
  display: flex;
  gap: 1em;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: ${(props) => props.theme.colors.primaryBlack};
    background-color: ${(props) => props.theme.colors.lightGray};
    z-index: 1;
  }
`;

export default withGreyBackground(UserSettings);
